import { Injectable } from '@angular/core';
import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  constructor(private translateService: TranslateService) {}
  static SW_ERROR_ALERT = Swal.mixin({
    confirmButtonText: 'Entendido',
    showCancelButton: false,
    icon: 'error',
  });

  readonly swGeneralAlert = Swal.mixin({
    confirmButtonText: 'Entendido',
    // cancelButtonText: 'Cancelar',
    confirmButtonColor: '#263238',
    showCancelButton: false,
  });

  readonly swErrorAlert = Swal.mixin({
    confirmButtonText: 'Entendido',
    showCancelButton: false,
    icon: 'error',
  });

  error(
    message: string,
    title = 'ALERT.ERROR',
    options: {} = {}
  ): Promise<SweetAlertResult> {
    return this.swErrorAlert.fire({
      title: this.translateService.instant(title),
      text: message,
      icon: 'error',
      confirmButtonText: this.translateService.instant('ALERT.OK'),
      cancelButtonText: this.translateService.instant('BUTTON.CANCEL'),
      ...options,
    });
  }

  errorWithReload(
    message: string,
    title = 'ALERT.ERROR',
    options: {} = {}
  ): void {
    this.error(message, title, options).then(() => {
      window.location.reload();
    });
  }

  warning(
    message: string,
    title = 'ALERT.WARNING',
    options: SweetAlertOptions = {}
  ): Promise<SweetAlertResult> {
    return this.swGeneralAlert.fire({
      title: this.translateService.instant(title),
      text: message,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.translateService.instant('ALERT.CONFIRM'),
      cancelButtonText: this.translateService.instant('BUTTON.CANCEL'),
      focusConfirm: true,
      ...options,
    });
  }

  question(
    message: string,
    title = 'ALERT.ATTENTION',
    options: SweetAlertOptions = {}
  ): Promise<SweetAlertResult> {
    return this.swGeneralAlert.fire({
      title: this.translateService.instant(title),
      text: message,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: this.translateService.instant('ALERT.CONFIRM'),
      cancelButtonText: this.translateService.instant('BUTTON.CANCEL'),
      focusConfirm: true,
      ...options,
    });
  }

  information<Type>(
    message: string,
    title = 'ALERT.ATTENTION',
    options: SweetAlertOptions = {}
  ): Promise<SweetAlertResult<Type>> {
    return this.swGeneralAlert.fire({
      title: this.translateService.instant(title),
      text: message,
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: this.translateService.instant('ALERT.CONFIRM'),
      cancelButtonText: this.translateService.instant('ALERT.CANCEL'),
      focusConfirm: true,
      ...options,
    }) as Promise<SweetAlertResult<Type>>;
  }

  success(
    message: string,
    title = 'ALERT.SUCCESS',
    options: SweetAlertOptions = {}
  ): Promise<SweetAlertResult> {
    return this.swGeneralAlert.fire({
      title: this.translateService.instant(title),
      text: message,
      icon: 'success',
      showCancelButton: false,
      confirmButtonText: this.translateService.instant('ALERT.OK'),
      focusConfirm: true,
      timer: 3000,
      timerProgressBar: true,
      ...options,
    });
  }

  askForNumber(
    message: string,
    title: string,
    options: SweetAlertOptions = {}
  ): Promise<SweetAlertResult<number>> {
    return this.swGeneralAlert.fire({
      title,
      text: message,
      input: 'number',
      inputAttributes: {
        autocapitalize: 'off',
      },
      showCancelButton: true,
      cancelButtonText: this.translateService.instant('BUTTON.CANCEL'),
      confirmButtonText: this.translateService.instant('BUTTON.SAVE'),
      allowOutsideClick: () => !Swal.isLoading(),
    });
  }

  smallError(
    message: string,
    title = 'ALERT.ERROR',
    options: SweetAlertOptions = {}
  ): Promise<SweetAlertResult> {
    return this.swErrorAlert.fire({
      title: `<strong style="font-size: 14px; margin: 2px 0;">${this.translateService.instant(
        title
      )}</strong>`,
      html: `<span style="font-size: 12px; display: block; margin: 2px 0;">${message}</span>`,
      icon: 'error',
      iconColor: '#ffffff',
      background: '#f02005',
      color: '#ffffff',
      showConfirmButton: false,
      toast: true,
      position: 'top-end',
      timer: 5000,
      timerProgressBar: true,
      showCloseButton: true,
      customClass: {
        popup: 'swal2-small-popup',
      },
      ...options,
      willOpen: () => {
        this.adjustSwalStyles();
      },
    });
  }

  smallInformation<Type>(
    message: string,
    title = 'ALERT.ATTENTION',
    options: SweetAlertOptions = {}
  ): Promise<SweetAlertResult<Type>> {
    return this.swGeneralAlert.fire({
      title: `<strong style="font-size: 14px; margin: 2px 0;">${this.translateService.instant(
        title
      )}</strong>`,
      html: `<span style="font-size: 12px; display: block; margin: 2px 0;">${message}</span>`,
      icon: 'info',
      iconColor: '#ffffff',
      background: '#07ebf7',
      color: '#000000',
      showCancelButton: false,
      showConfirmButton: false,
      // confirmButtonText: this.translateService.instant('ALERT.CONFIRM'),
      // cancelButtonText: this.translateService.instant('ALERT.CANCEL'),
      focusConfirm: true,
      toast: true,
      position: 'top-end',
      timer: 5000,
      timerProgressBar: true,
      showCloseButton: true,
      customClass: {
        popup: 'swal2-small-popup',
      },
      ...options,
      willOpen: () => {
        this.adjustSwalStyles();
      },
    }) as Promise<SweetAlertResult<Type>>;
  }

  smallSuccess(
    message: string,
    title = 'ALERT.SUCCESS',
    options: SweetAlertOptions = {}
  ): Promise<SweetAlertResult> {
    return this.swGeneralAlert.fire({
      title: `<strong style="font-size: 14px; margin: 2px 0;">${this.translateService.instant(
        title
      )}</strong>`,
      html: `<span style="font-size: 12px; display: block; margin: 2px 0;">${message}</span>`,
      icon: 'success',
      iconColor: '#ffffff',
      background: '#329542',
      color: '#ffffff',
      showCancelButton: false,
      showConfirmButton: false,
      // confirmButtonText: this.translateService.instant('ALERT.OK'),
      toast: true,
      position: 'top-end',
      timer: 5000,
      timerProgressBar: true,
      showCloseButton: true,
      customClass: {
        popup: 'swal2-small-popup',
      },
      ...options,
      willOpen: () => {
        this.adjustSwalStyles();
      },
    });
  }

  adjustSwalStyles(): void {
    setTimeout(() => {
      const popup = document.querySelector('.swal2-small-popup') as HTMLElement;

      if (popup) {
        popup.style.padding = '6px 10px';
        popup.style.minHeight = '40px';
        popup.style.width = '250px';
      }
    }, 10);
  }
}
